import type { AIOperation, MindMapNode, MindMapEdge } from '@bandos/shared'
import type { SourceNodeInfo, AIStateSetters, AIRefs } from '../types/aiTypes'
import { createSuggestions, mergeSuggestions, removeSuggestionsForNode } from './aiSuggestions'

// ============================================================================
// ADD SUGGESTIONS TO UI
// ============================================================================

interface AddSuggestionsCallbacks {
  onReady?: (nodeId: string, count: number) => void
  onRegenerateFeedbackForChildren?: (sourceNodeId: string) => void
}

/**
 * Adds newly generated AI suggestions to local state.
 * Manages batch IDs for "generate more" pagination.
 */
export function addSuggestionsToUI(
  operation: string,
  nodes: MindMapNode[],
  edges: MindMapEdge[],
  nodeId: string,
  preserveSuggestions: boolean,
  suggestionIds: string[] | undefined,
  userFeedback: string | undefined,
  setters: AIStateSetters,
  refs: AIRefs,
  sourceNodes: Map<string, SourceNodeInfo>,
  callbacks: AddSuggestionsCallbacks
): void {
  const newBatchId = preserveSuggestions ? refs.batchIdRef.current + 1 : 0
  refs.batchIdRef.current = newBatchId

  const newSuggestions = createSuggestions(nodes, edges, newBatchId, suggestionIds)

  const suggestionsWithFeedback = userFeedback?.trim()
    ? newSuggestions.map((s) => ({ ...s, userFeedback: userFeedback.trim() }))
    : newSuggestions

  setters.setSuggestions((prev) =>
    mergeSuggestions(prev, suggestionsWithFeedback, nodeId, preserveSuggestions)
  )

  // Update source node info
  if (nodeId) {
    setters.setSourceNodes((prev) => {
      const updated = new Map(prev)
      const existing = updated.get(nodeId)
      const existingCount = existing?.suggestionCount ?? 0
      const shouldAppend = preserveSuggestions && existingCount > 0
      const totalCount = shouldAppend && existing ? existingCount + nodes.length : nodes.length

      updated.set(nodeId, {
        node: existing?.node ?? nodes[0],
        operation: operation as AIOperation,
        isLoading: false,
        error: null,
        suggestionCount: totalCount,
        lastUpdated: Date.now(),
        viewed: false,
        feedbackHistory: existing?.feedbackHistory ?? [],
      })
      return updated
    })
  }

  refs.lastOperationRef.current = operation
  refs.lastNodeIdRef.current = nodeId
  setters.setCurrentOperation(operation as AIOperation)
  setters.setPendingOperations((prev) => {
    const updated = new Set(prev)
    updated.delete(operation as AIOperation)
    if (updated.size === 0) {
      setters.setIsProcessing(false)
    }
    return updated
  })
  setters.setError(null)

  // Notify that suggestions are ready
  if (nodeId && nodes.length > 0) {
    const existing = sourceNodes.get(nodeId)
    const existingCount = existing?.suggestionCount ?? 0
    const shouldAppend = preserveSuggestions && existingCount > 0
    const totalCount = shouldAppend ? existingCount + nodes.length : nodes.length

    callbacks.onReady?.(nodeId, totalCount)

    if (operation === 'generate_idea') {
      callbacks.onRegenerateFeedbackForChildren?.(nodeId)
    }
  }
}

// ============================================================================
// START OPERATION UI STATE
// ============================================================================

/**
 * Initializes UI state when an AI operation begins.
 * Clears old suggestions if not preserving, updates source node metadata.
 */
export function startOperationUI(
  operation: AIOperation,
  nodeId: string,
  node: MindMapNode | undefined,
  preserveSuggestions: boolean,
  userFeedback: string | undefined,
  setters: AIStateSetters
): void {
  if (!preserveSuggestions && nodeId) {
    setters.setSuggestions((prev) => removeSuggestionsForNode(prev, nodeId))
  } else if (!preserveSuggestions && !nodeId) {
    setters.setSuggestions([])
  }

  if (node && nodeId) {
    setters.setSourceNodes((prev) => {
      const updated = new Map(prev)
      const existing = updated.get(nodeId)

      let feedbackHistory = existing?.feedbackHistory ?? []
      if (userFeedback && userFeedback.trim()) {
        feedbackHistory = preserveSuggestions
          ? [...feedbackHistory, userFeedback.trim()]
          : userFeedback.trim()
            ? [userFeedback.trim()]
            : []
      } else if (!preserveSuggestions) {
        feedbackHistory = []
      }

      updated.set(nodeId, {
        node: node,
        operation: operation,
        isLoading: true,
        error: null,
        suggestionCount: preserveSuggestions ? (existing?.suggestionCount ?? 0) : 0,
        lastUpdated: Date.now(),
        viewed: false,
        feedbackHistory,
      })
      return updated
    })
  }

  setters.setCurrentOperation(operation)
  setters.setIsProcessing(true)
  setters.setPendingOperations((prev) => new Set([...prev, operation]))
  setters.setError(null)
}

// ============================================================================
// SET ERROR IN UI
// ============================================================================

/**
 * Sets an error state, either for a specific source node or globally.
 */
export function setErrorInUI(
  errorMessage: string,
  nodeId: string | undefined,
  setters: AIStateSetters
): void {
  if (nodeId) {
    setters.setSourceNodes((prev) => {
      const updated = new Map(prev)
      const existing = updated.get(nodeId)
      if (existing) {
        updated.set(nodeId, { ...existing, isLoading: false, error: errorMessage })
      }
      return updated
    })
  } else {
    setters.setError(errorMessage)
  }
  setters.setIsProcessing(false)
  setters.setPendingOperations(new Set())
}
